import {
  useQuery,
  skipToken,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { IncomingHttpHeaders } from 'http';
import StatusCode from 'status-code-enum';

import { donedealGateway } from '../../../../../../../api/api-utils/commonApiUtils';

import { formatAuthorizationHeader } from '../../../../../../../helpers/auth';
import { useUserContext } from '../../../../../../../contexts/UserContext';

import { userVehicleSchema } from '../schema/UserVehicle.schema';
import { type MileageUnitsLowercase } from '../schema/SharedSchema';

import { PAGE } from '../../../../../../../helpers/pages';
import getConfig from 'next/config';

type PutUserVehicleMutationArgs = {
  registrationNumber: string;
  mileage: number;
  mileageUnit: MileageUnitsLowercase;
};

type PutUserVehicleRequestArgs = PutUserVehicleMutationArgs & {
  headers: IncomingHttpHeaders;
};

const getUserVehicle = async (headers: IncomingHttpHeaders) => {
  const {
    publicRuntimeConfig: { API_CLIENT_TIMEOUT },
  } = getConfig();
  const { data, status } = await donedealGateway.get(
    '/ddapi/v1/users/vehicle/valuation',
    {
      headers,
      timeout: API_CLIENT_TIMEOUT,
    },
  );

  return userVehicleSchema.parse(
    status === StatusCode.SuccessNoContent ? null : data,
  );
};

const putUserVehicle = async (args: PutUserVehicleRequestArgs) => {
  const {
    publicRuntimeConfig: { API_CLIENT_TIMEOUT },
  } = getConfig();
  const { headers, ...rest } = args;

  return donedealGateway.put(
    '/ddapi/v1/users/vehicle',
    { ...rest },
    {
      headers,
      timeout: API_CLIENT_TIMEOUT,
    },
  );
};

const deleteUserVehicle = async (headers: IncomingHttpHeaders) => {
  const {
    publicRuntimeConfig: { API_CLIENT_TIMEOUT },
  } = getConfig();
  return donedealGateway.delete('/ddapi/v1/users/vehicle', {
    headers,
    timeout: API_CLIENT_TIMEOUT,
  });
};

export const useUserVehicleQueryKeys = {
  get: ['getUserVehicle'] as const,
};

export const useUserVehicleQuery = () => {
  const { accessToken } = useUserContext();

  const headers = formatAuthorizationHeader(accessToken);

  const useGetUserVehicleQuery = () =>
    useQuery({
      queryKey: useUserVehicleQueryKeys.get,
      queryFn: accessToken ? () => getUserVehicle(headers) : skipToken,
      staleTime: Infinity,
      meta: { noToast: true, tags: [PAGE.AD_DETAILS] },
    });

  const usePutUserVehicleQuery = ({ onSuccess }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: ({
        registrationNumber,
        mileage,
        mileageUnit,
      }: PutUserVehicleMutationArgs) =>
        putUserVehicle({ registrationNumber, mileage, mileageUnit, headers }),
      meta: { noToast: true, tags: [PAGE.AD_DETAILS] },
      onSuccess,
    });

  const useDeleteUserVehicleQuery = (
    options: UseMutationOptions<unknown, Error, void, unknown>,
  ) =>
    useMutation({
      mutationFn: () => deleteUserVehicle(headers),
      meta: {
        tags: [PAGE.AD_DETAILS],
        errorMessage: 'Error removing user vehicle',
      },
      ...options,
    });

  return {
    useGetUserVehicleQuery,
    usePutUserVehicleQuery,
    useDeleteUserVehicleQuery,
  };
};
